import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FaPlus, FaUserShield, FaUser, FaTimes, FaSave } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import AdminNav from '../../components/AdminNav';
import api from '../../utils/api';
import './AdminUsers.css';

const AdminUsers = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.post('/users/admin', formData);
      setSuccess('Admin user created successfully!');
      fetchUsers();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error creating admin:', error);
      setError(error.response?.data?.message || 'Failed to create admin user');
    }
  };

  const toggleAdminStatus = async (userId) => {
    if (window.confirm('Are you sure you want to change this user\'s admin status?')) {
      try {
        const response = await api.put(`/users/${userId}/admin`);
        setSuccess(response.data.message);
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error toggling admin status:', error);
        setError('Failed to update user status');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', phone: '' });
    setShowForm(false);
  };

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading users...</div>;
  }

  return (
    <>
      <AdminNav />
      <div className="admin-users">
        <div className="admin-header">
          <div>
            <h1>User Management</h1>
            <p>{users.length} total users ({users.filter(u => u.isAdmin).length} admins)</p>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              <FaPlus /> Create Admin User
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {showForm && (
          <div className="user-form-card">
            <div className="form-header">
              <h2>Create New Admin User</h2>
              <button onClick={resetForm} className="btn-close">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="admin@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength="6"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <FaSave /> Create Admin User
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(usr => (
                <tr key={usr._id}>
                  <td>
                    <div className="user-info">
                      <div className="user-icon">
                        {usr.isAdmin ? <FaUserShield /> : <FaUser />}
                      </div>
                      <strong>{usr.name}</strong>
                    </div>
                  </td>
                  <td>{usr.email}</td>
                  <td>{usr.phone || 'N/A'}</td>
                  <td>
                    <span className={`role-badge ${usr.isAdmin ? 'admin' : 'user'}`}>
                      {usr.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>{new Date(usr.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => toggleAdminStatus(usr._id)}
                      className={`btn-toggle ${usr.isAdmin ? 'demote' : 'promote'}`}
                      disabled={usr._id === user._id}
                    >
                      {usr._id === user._id ? 'You' : usr.isAdmin ? 'Remove Admin' : 'Make Admin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="no-users">
              <p>No users found</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminUsers;
